import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Email, Search, Send } from "@mui/icons-material";
import { formatDate, formatDateTime } from "@nomnom/core";
import type {
  WaitlistCampaign,
  WaitlistEntry,
  WaitlistRole,
  WaitlistStatus,
} from "../types/firestore";
import {
  buildWaitlistMetrics,
  buildWaitlistLaunchMetrics,
  filterWaitlistEntries,
  getWaitlistInviteUrlForRole,
  retryWaitlistConfirmation,
  sendWaitlistInvites,
  subscribeToWaitlistCampaigns,
  subscribeToWaitlistEntries,
  updateWaitlistStatus,
  type WaitlistRoleFilter,
  type WaitlistStatusFilter,
} from "../services/waitlistService";

const STATUS_OPTIONS: WaitlistStatusFilter[] = [
  "all",
  "active",
  "invited",
  "activated",
  "do_not_contact",
  "bounced",
];

export default function Waitlist() {
  const liveInvitesEnabled =
    process.env.REACT_APP_WAITLIST_LIVE_INVITES_ENABLED?.toLowerCase() === "true";
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [campaigns, setCampaigns] = useState<WaitlistCampaign[]>([]);
  const [entriesReady, setEntriesReady] = useState(false);
  const [campaignsReady, setCampaignsReady] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<WaitlistRoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<WaitlistStatusFilter>("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [campaignLabel, setCampaignLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToWaitlistEntries(
      (nextEntries) => {
        setEntries(nextEntries);
        setEntriesReady(true);
        setError(null);
      },
      (message) => {
        setEntriesReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToWaitlistCampaigns(
      (nextCampaigns) => {
        setCampaigns(nextCampaigns);
        setCampaignsReady(true);
      },
      (message) => {
        setCampaignsReady(true);
        setError(message);
      },
    );
  }, []);

  const loading = !(entriesReady && campaignsReady);
  const filteredEntries = useMemo(
    () => filterWaitlistEntries(entries, roleFilter, statusFilter, search),
    [entries, roleFilter, statusFilter, search],
  );
  const metrics = useMemo(() => buildWaitlistMetrics(entries), [entries]);
  const selectedEntries = useMemo(
    () => entries.filter((entry) => selectedIds.includes(entry.id)),
    [entries, selectedIds],
  );
  const selectedRole = useMemo<WaitlistRole | null>(() => {
    const roleSet = new Set(selectedEntries.map((entry) => entry.role));
    if (roleSet.size !== 1) {
      return null;
    }

    return selectedEntries[0]?.role || null;
  }, [selectedEntries]);
  const launchMetrics = useMemo(
    () => buildWaitlistLaunchMetrics(entries, selectedEntries, liveInvitesEnabled),
    [entries, liveInvitesEnabled, selectedEntries],
  );

  const toggleSelection = (entryId: string) => {
    setSelectedIds((current) =>
      current.includes(entryId)
        ? current.filter((id) => id !== entryId)
        : [...current, entryId],
    );
  };

  const toggleAllVisible = () => {
    if (filteredEntries.length === 0) {
      return;
    }

    const visibleIds = filteredEntries.map((entry) => entry.id);
    const allVisibleSelected = visibleIds.every((id) => selectedIds.includes(id));

    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds])),
    );
  };

  const resetInviteDialog = () => {
    setInviteDialogOpen(false);
    setCampaignLabel("");
    setCtaUrl("");
    setCustomNote("");
  };

  const handleStatusUpdate = async (
    entryId: string,
    status: WaitlistStatus,
  ) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateWaitlistStatus(entryId, status);
      setSuccess(`Updated waitlist entry to ${status.replaceAll("_", " ")}.`);
    } catch (updateError) {
      setError(
        updateError instanceof Error
          ? updateError.message
          : "Failed to update waitlist entry.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryConfirmation = async (entryId: string) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await retryWaitlistConfirmation(entryId);
      setSuccess("Confirmation email retry queued successfully.");
    } catch (retryError) {
      setError(
        retryError instanceof Error
          ? retryError.message
          : "Failed to retry confirmation email.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInvites = async () => {
    if (!selectedRole) {
      setError("Select waitlist entries from a single role before sending invites.");
      return;
    }

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await sendWaitlistInvites({
        role: selectedRole,
        entryIds: selectedEntries.map((entry) => entry.id),
        campaignLabel,
        ctaUrl,
        customNote: customNote || undefined,
      });

      setSelectedIds([]);
      resetInviteDialog();
      setSuccess(
        result.failedCount > 0
          ? `Sent ${result.sentCount} invites. ${result.failedCount} recipients failed.`
          : `Sent ${result.sentCount} invites successfully.`,
      );
    } catch (inviteError) {
      setError(
        inviteError instanceof Error
          ? inviteError.message
          : "Failed to send waitlist invites.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4">Waitlist</Typography>
          <Typography variant="body2" color="text.secondary">
            Student waitlist operations plus rider application review metadata
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Send />}
          disabled={
            !liveInvitesEnabled ||
            selectedIds.length === 0 ||
            !selectedRole ||
            actionLoading
          }
          onClick={() => {
            setError(null);
            setSuccess(null);
            if (selectedRole) {
              setCtaUrl(getWaitlistInviteUrlForRole(selectedRole));
            }
            setInviteDialogOpen(true);
          }}
        >
          {liveInvitesEnabled ? "Send invites" : "Invites locked"}
        </Button>
      </Box>

      {!liveInvitesEnabled && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning">
            Live invite sends are disabled until launch. Use the daily canary and test inboxes
            for email checks, then enable
            {" "}
            <code>WAITLIST_LIVE_INVITES_ENABLED</code>
            {" "}
            in Functions and
            {" "}
            <code>REACT_APP_WAITLIST_LIVE_INVITES_ENABLED</code>
            {" "}
            in admin when you are ready to send real user invites.
          </Alert>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        {metrics.map((metric) => (
          <Card key={metric.label} sx={{ minWidth: 160, flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {metric.label}
              </Typography>
              <Typography variant="h4">{metric.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Invite Readiness</Typography>
            <Typography variant="body2" color="text.secondary">
              Existing waitlist entries stay untouched until an admin explicitly sends
              invites or changes a row status.
            </Typography>
          </Box>
          <Card variant="outlined" sx={{ minWidth: 170 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Student cohort
              </Typography>
              <Typography variant="h5">
                {launchMetrics.recommendedStudentCohortSize}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                from {launchMetrics.confirmedStudents} confirmed active students
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 170 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Rider pool
              </Typography>
              <Typography variant="h5">{launchMetrics.confirmedRiders}</Typography>
              <Typography variant="caption" color="text.secondary">
                confirmed active riders
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ minWidth: 170 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Selection ready
              </Typography>
              <Typography variant="h5">{launchMetrics.selectedReadyCount}</Typography>
              <Typography variant="caption" color="text.secondary">
                active + confirmed
              </Typography>
            </CardContent>
          </Card>
        </Stack>
        <Alert
          severity={launchMetrics.inviteLocked ? "warning" : "success"}
          sx={{ mt: 2 }}
        >
          {launchMetrics.inviteLocked
            ? "Live invite sends are locked. You can safely review cohorts without emailing real users."
            : "Live invite sends are enabled. Double-check selected role and campaign label before sending."}
        </Alert>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            placeholder="Search waitlist..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value as WaitlistRoleFilter)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All roles</MenuItem>
            <MenuItem value="student">Student</MenuItem>
            <MenuItem value="rider">Rider</MenuItem>
          </TextField>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as WaitlistStatusFilter)}
            sx={{ minWidth: 180 }}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status === "all" ? "All statuses" : status.replaceAll("_", " ")}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    filteredEntries.length > 0 &&
                    filteredEntries.every((entry) => selectedIds.includes(entry.id))
                  }
                  indeterminate={
                    filteredEntries.some((entry) => selectedIds.includes(entry.id)) &&
                    !filteredEntries.every((entry) => selectedIds.includes(entry.id))
                  }
                  onChange={toggleAllVisible}
                  inputProps={{ "aria-label": "Select all visible waitlist entries" }}
                />
              </TableCell>
              <TableCell>Person</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Confirmation</TableCell>
              <TableCell>Invites</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary" align="center" py={4}>
                    No waitlist entries found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(entry.id)}
                      onChange={() => toggleSelection(entry.id)}
                      inputProps={{ "aria-label": `Select ${entry.fullName}'s row` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {entry.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.email}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Source: {entry.heardAboutNomNom || "Unknown"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={entry.role}
                      color={entry.role === "student" ? "primary" : "secondary"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.role === "student"
                        ? entry.studentProfile?.dormOrArea || "No dorm set"
                        : entry.riderProfile?.transportMethod || "No transport set"}
                    </Typography>
                    {entry.role === "rider" && (
                      <Typography variant="caption" color="text.secondary">
                        {entry.riderProfile?.typicalAvailability || "No availability set"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={entry.status.replaceAll("_", " ")} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.confirmationDelivery?.state || "pending"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.confirmationDelivery?.lastAttemptAt
                        ? formatDateTime(entry.confirmationDelivery.lastAttemptAt, "Unknown")
                        : "Not attempted"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{entry.inviteCount || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.lastInvitedAt
                        ? formatDate(entry.lastInvitedAt, "Unknown")
                        : "Never invited"}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(entry.createdAt, "Unknown")}</TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <Button
                        size="small"
                        onClick={() => handleStatusUpdate(entry.id, "activated")}
                        disabled={actionLoading}
                      >
                        Activate
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleStatusUpdate(entry.id, "do_not_contact")}
                        disabled={actionLoading}
                      >
                        Do not contact
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleRetryConfirmation(entry.id)}
                        disabled={actionLoading}
                      >
                        Retry email
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Campaign history
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Campaign</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Failed</TableCell>
              <TableCell>Requested</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" align="center" py={2}>
                    No waitlist campaigns yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.campaignLabel}</TableCell>
                  <TableCell>{campaign.role}</TableCell>
                  <TableCell>{campaign.status}</TableCell>
                  <TableCell>{campaign.sentCount}</TableCell>
                  <TableCell>{campaign.failedCount}</TableCell>
                  <TableCell>{formatDateTime(campaign.requestedAt, "Unknown")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={inviteDialogOpen} onClose={resetInviteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Send waitlist invites</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedEntries.length} {selectedRole || "mixed-role"} entries selected
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Campaign label"
              value={campaignLabel}
              onChange={(event) => setCampaignLabel(event.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Invite destination"
              value={ctaUrl}
              required
              fullWidth
              InputProps={{ readOnly: true }}
              helperText={
                selectedRole
                  ? `Locked to the canonical ${selectedRole} invite route.`
                  : "Select entries from a single role to choose a destination."
              }
            />
            <TextField
              label="Custom note"
              value={customNote}
              onChange={(event) => setCustomNote(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetInviteDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleSendInvites} variant="contained" disabled={actionLoading}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
